import React, {useContext, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getOrders} from "../../actions/orderActions";
import {GetOrdersRequest} from "../../model/GetOrdersRequest";
import PtActivityIndicator from "../core/pt-activity-indicator/pt-activity-indicator";
import PtOrderHistorySearchFilterWrapper from "./pt-order-history-search-filter-wrapper/pt-order-history-search-filter-wrapper";
import {getTranslation} from "../../helpers/getTranslation";
import {View, TouchableOpacity} from "react-native";
import PtText from "../core/pt-text/pt-text";
import {styleVars} from "../../app-style-vars";
import PtOrderSingle from "./pt-order-single/pt-order-single";
import PtCallout from "../core/pt-callout/pt-callout";
import {useNavigation} from "@react-navigation/native";
import {PtOrderHistoryProps} from './pt-order-history-props';
import { styles } from './pt-order-history-page-styles';
import UserContext from "../../helpers/UserContext";

const tomorrow = new Date().setDate(new Date().getDate() + 1);
const defaultMinimumDate = new Date(new Date(tomorrow).setFullYear(new Date(tomorrow).getFullYear() - 1));
const defaultMaximumDate = new Date(); /** today */

export default function PtOrderHistoryPage(props: PtOrderHistoryProps) {

  /** labels */
  const noResultsText = props.noResultsText || getTranslation('order.history.no.results.text') || 'No results are found. Please use the search for specific orders or filter accordingly';
  const noResultsTextLimited = props.noResultsTextLimited || getTranslation('order.history.limited.no.results.text') || 'No orders found';
  const resultsFoundText = props.resultsFoundText || getTranslation('order.history.results.found.text') || 'results found';
  const errorMessage = props.errorMessage || getTranslation('order.history.orders.error.message') || 'Error encountered while collecting orders';
  const showAllOrdersText = props.showAllOrdersText || getTranslation('order.history.show.all.orders.text') || 'Show all orders';

  const limitedModeTitle = props.limitedModeTitle || getTranslation ('order.history.limited.mode.title') || 'Your latest orders';
  const limitedModeSubTitle = props.limitedModeSubTitle || getTranslation('order.history.limited.mode.sub.title') || 'during the last 7 days';

  const loadingLabel = props.loadingLabel || getTranslation('order.history.loading.label') || 'Loading orders';

  const limitedMode = props.limitedMode || false;
  const [limitedOrders, setLimitedOrders] = useState(null);
  const [limitedOrdersCount, setLimitedOrdersCount] = useState(null);

  const initialStartDate = props.startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // seven days ago
  const initialEndDate = props.endDate || new Date(); // today
  const initialPageSize = props.pageSize || 20;
  const initialSortBy = props.sortBy || 'orderNo.desc';

  const hideSearchAndFilters = props.hideSearchAndFilters || false;
  const hideLoadMore = props.hideLoadMore || false;

  const minimumDate = props.minimumDate || defaultMinimumDate;
  const maximumDate = props.maximumDate || defaultMaximumDate;

  const navigation = useNavigation();
  const {userData, updateUserData} = useContext(UserContext);

  const impersonationOK = !userData.mustImpersonate || (userData.mustImpersonate && userData.impersonatingId !== null);

  const searchOptions = [
    {value: 'orderNo', label: getTranslation('order.history.search.options.order.no')},
    {value: 'invoiceNo', label: getTranslation('order.history.search.options.invoice.no')},
    {value: 'productId', label: getTranslation('order.history.search.options.product.id')},
    {value: 'purchaseOrderNo', label: getTranslation('order.history.search.options.purchase.order.number')}
  ];

  const filtersOptions = {
    orderStatus: {
      title: getTranslation('order.history.filters.status.title'),
      choices: [
        {value: '', label: getTranslation('order.history.filters.status.any')},
        {value: 'inAnalysis', label: getTranslation('order.history.filters.status.in.analysis')},
        {value: 'inTransit', label: getTranslation('order.history.filters.status.in.transit')},
        {value: 'open', label: getTranslation('order.history.filters.status.open')},
        {value: 'inProgress', label: getTranslation('order.history.filters.status.in.progress')},
        {value: 'inShipment', label: getTranslation('order.history.filters.status.in.shipment')},
        {value: 'delivered', label: getTranslation('order.history.filters.status.delivered')}
      ]
    },
    orderDate: {
      title: getTranslation('order.history.filters.date.title'),
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // seven days ago
      minDate: defaultMinimumDate,
      endDate: new Date(),
      maxDate: new Date(),
      startDateLabel: getTranslation('order.history.filters.date.from.label'),
      endDateLabel: getTranslation('order.history.filters.date.to.label')
    }
  }

  const loadMoreLabel = props.loadMoreLabel || getTranslation('order.history.load.more.text');

  /** action related stuff */
  const PTBaseSiteUID = props.baseSiteUID || userData.baseSiteUID;
  const PTSalesAreaDependantCustomerUid = props.salesAreaDependantCustomerUid || userData.salesAreaDependantCustomerUid;
  const currentLanguage = useSelector((state:any) => state.translationsReducer.currentLanguage);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state:any) => state.authReducer.isAuthenticated);
  const initialRequestSent = useSelector((state:any) => state.ordersReducer.initialDataCollected);
  const loadingOrders = useSelector((state:any) => state.ordersReducer.loading);
  const requestForOrders = useSelector((state:any) => state.ordersReducer.request);
  const orders = useSelector((state:any) => state.ordersReducer.orders);
  const ordersCount = useSelector((state:any) => state.ordersReducer.totalNoOfOrders);
  const errors = useSelector((state:any) => state.ordersReducer.errors);

  /** keep all search and filtering parameters in a state at top level
   * and pass them down to other components as props
   * when something is changed in child components, pass all the way to the top
   * via handler functions in props, then change state on top level
   * it should propagate back down to child component via props
   */
  const requestInitialState = {
    pageSize: initialPageSize,
    offset: null,
    startDate: initialStartDate,
    endDate: initialEndDate,
    orderNo: null,
    invoiceNo: null,
    productId: null,
    purchaseOrderNo: null,
    orderReason: null,
    rejectionReason: null,
    orderStatus1: null,
    orderStatus2: null,
    orderStatus3: null,
    orderStatus4: null,
    orderStatus5: null,
    orderStatus6: null,
    orderStatus7: null,
    orderStatus8: null,
    process: null,
    brand: null,
    reason: null,
    sortBy: initialSortBy,
    searchType: 'orderNo',
    searchTerm: ''
  }
  const [requestParams, setRequestParams] = useState(requestInitialState);

  useEffect(() => {
    if(isAuthenticated && impersonationOK) {
      if (!initialRequestSent && orders.length === 0) {
        const initialRequest = new GetOrdersRequest(initialStartDate, initialEndDate, initialPageSize, initialSortBy);
        getOrders(dispatch, initialRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage)
          .then((response) => {
            if(limitedMode) {
              console.log('setting limitedOrders', response);
              setLimitedOrders(response?.orders);
              setLimitedOrdersCount(response?.totalNoOfOrder)
            }
          })
          .finally(() => {
          console.log('the req after initial = ', requestForOrders);
        });
      }
    } else {
      setLimitedOrders(null);
    }
  },[initialRequestSent, isAuthenticated, impersonationOK])

  /** handlers for various props that have to be passed down the line and listened to back here */
  /** end date filter changed */
  const endDateChangedHandler = (data:any) => {
    console.log('order history page, endDateChangedHandler, new value = ', data.newDate);
    setRequestParams({...requestParams, endDate: data.newDate});
  }
  /** start date filter changed */
  const startDateChangedHandler = (data:any) => {
    console.log('order history page, startDateChangedHandler, new value = ', data.newDate);
    setRequestParams({...requestParams, startDate: data.newDate});
  }
  /** order status filter changed */
  const orderStatusFilterChangedHandler = (data:any) => {
    console.log('order history page, orderStatusFilterChangedHandler, new value = ', data.newValue);
    setRequestParams({...requestParams, orderStatus1: data.newValue})
  }
  /** search type changed */
  const searchTypeChangedHandler = (data:any) => {
    console.log('order history page, searchTypeChangedHandler, new value = ', data.searchType);
    setRequestParams({...requestParams, searchType: data.searchType})
  }
  /** search term changed */
  const searchTermChangedHandler = (data:any) => {
    if(data.searchTerm == '') {data.searchTerm = null;}
    console.log('order history page, searchTermChangedHandler, new value = ', data.searchTerm)
    setRequestParams({...requestParams, searchTerm: data.searchTerm})
  }
  /** search initiated */
  const searchHandler = () => {
    /** search should set order status to initial value, time range to full span, page to initial, searchtype to searchterm and then send request */
    /** search request should not contain dates */
    setRequestParams({
      ...requestParams,
      startDate: minimumDate,
      endDate: maximumDate,
      offset: null,
      orderStatus1: null,
      [requestParams.searchType]:requestParams.searchTerm
    });
    const searchRequest = new GetOrdersRequest(null, null, initialPageSize, requestForOrders.sortBy);
    searchRequest[requestParams.searchType] = requestParams.searchTerm;
    searchRequest.startDate = minimumDate;
    searchRequest.endDate = maximumDate;
    searchRequest.orderStatus1 = requestParams.orderStatus1;
    getOrders(dispatch, searchRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }
  /** apply filters */
  const applyFiltersHandler = () => {
    /** apply filters should modify last request by setting date range, orderStatus1, search criteria and setting offset to null */
    setRequestParams({...requestParams, offset: null});
    const filterRequest = requestForOrders;
    filterRequest[requestParams.searchType] = requestParams.searchTerm;
    filterRequest.startDate = requestParams.startDate;
    filterRequest.endDate = requestParams.endDate;
    filterRequest.orderStatus1 = requestParams.orderStatus1;
    filterRequest.offset = null;
    getOrders(dispatch, filterRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }
  /** reset filters and search */
  const resetHandler = () => {
    /** reset should trigger initial request*/
    setRequestParams(requestInitialState);
    const resetRequest = new GetOrdersRequest(initialStartDate, initialEndDate, initialPageSize, initialSortBy);
    getOrders(dispatch, resetRequest, false, null, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }

  /** load more */
  const loadMoreOrders = () => {
    const loadMoreRequest = requestForOrders;
    const newOffset = orders.length + 1;
    const newPageSize = 10;
    setRequestParams({...requestParams, offset: newOffset, pageSize: newPageSize});
    loadMoreRequest.offset = newOffset;
    loadMoreRequest.pageSize = newPageSize;
    getOrders(dispatch, loadMoreRequest, true, orders, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage);
  }

  /** go to orders page */
  const showAllOrders = () => {
    navigation.navigate('orderHistoryScreen', {activeTab: 'orders'});
  }

  return (
    <>
      {isAuthenticated ?
        <>
          <PtActivityIndicator show={loadingOrders} message={loadingLabel}/>
          <View style={{zIndex: 10}}>

            <PtOrderHistorySearchFilterWrapper
              hide = {hideSearchAndFilters}
              searchOptions={searchOptions}
              filtersOptions={filtersOptions}
              requestParams={requestParams}
              orderStatusFilterChangedHandler={orderStatusFilterChangedHandler}
              startDateChangedHandler={startDateChangedHandler}
              endDateChangedHandler={endDateChangedHandler}
              applyFiltersHandler={applyFiltersHandler}
              resetHandler={resetHandler}
              searchTermChangedHandler={searchTermChangedHandler}
              searchTypeChangedHandler={searchTypeChangedHandler}
              searchHandler={searchHandler}
              ordersCount={ordersCount}
            />
          </View>
          <View>
            {!limitedMode && !errors ?
              <View style={[styles.ordersCountWrapper]}>
                {ordersCount == 0 ?
                  <View style={[styles.noResultsContainer]}>
                    <PtText style={[styles.noResultsText]}>{noResultsText}</PtText>
                  </View>
                  :
                  <PtText style={[styles.ordersCountText]}>{ordersCount} {resultsFoundText}</PtText>
                }
              </View>
              : null
            }
              <View>
                { !limitedMode ?
                  !errors ?
                    <>
                      {orders?.map((order: any, idx: number) => (
                        <PtOrderSingle order={order} key={`single-order-${idx}`}></PtOrderSingle>
                      ))}
                    </>
                    :
                    <View>
                      <PtCallout type={'error'} message={errorMessage}/>
                    </View>
                  :
                  limitedMode ?
                    <>
                      <View>
                        <PtText style={[styles.limitedModeTitleText]}>{limitedModeTitle}</PtText>
                        <PtText style={[styles.limitedModeSubTitleText]}>{limitedModeSubTitle}</PtText>
                      </View>

                      {limitedOrders ?
                        <>
                          {limitedOrders?.map((order: any, idx: number) => (
                            idx < initialPageSize ?
                              <PtOrderSingle order={order} key={`single-order-${idx}`} limitedMode></PtOrderSingle>
                              : null
                          ))}
                        </>
                        : null
                      }

                      {limitedOrdersCount == 0 ?
                        <View style={[styles.noResultsContainer]}>
                          <PtText style={[styles.noResultsText]}>
                            {noResultsTextLimited}
                          </PtText>
                        </View>
                        : null
                      }

                      <View style={[styles.showAllContainer]}>
                        <TouchableOpacity onPress={() => showAllOrders()}>
                          <PtText style={[styles.showAllText]}>
                            {showAllOrdersText}
                          </PtText>
                        </TouchableOpacity>
                      </View>
                    </>
                    : null
                }
                {!hideLoadMore && !loadingOrders && (ordersCount > (requestParams.offset || 0) + requestParams.pageSize) ?
                  <View style={[styles.loadMoreContainer]}>
                    <TouchableOpacity onPress={() => loadMoreOrders()}>
                      <PtText style={[styles.loadMoreText]}>{loadMoreLabel}</PtText>
                    </TouchableOpacity>
                  </View>
                  : null
                }
              </View>
          </View>
        </>
        :
        <View>
          <PtText style={{fontSize:styleVars.rem(.625), textAlign:'center'}}>{'Feature not available. Please login to view your order history.'}</PtText>
        </View>
      }
    </>
  )
}
